import type { GanttFeature } from '@/components/kibo-ui/gantt'
import type { FerryPin } from '@/components/kibo-ui/gantt/ferry-markers'
import {
  buildFerryPins,
  buildFerrySegments,
} from '@/components/kibo-ui/gantt/ferry-markers'
import type { WorkPeriodGap } from '@/components/kibo-ui/gantt/work-period-gaps'
import { findWorkPeriodGaps } from '@/components/kibo-ui/gantt/work-period-gaps'
import {
  GanttFeatureList,
  GanttFeatureRow,
  GanttHeader,
  GanttMarker,
  GanttProvider,
  GanttSidebar,
  GanttSidebarItem,
  GanttTimeline,
} from '@/components/kibo-ui/gantt'
import { Time } from '@/lib/time/time'
import { DriverActivityType } from '@/modules/activities/consts'
import { enforceHourMinuteString } from '@/modules/activities'
import { useActivityTypeLabels } from '@/modules/activities/use-activity-type-labels'
import {
  TOGGLE_ACTIVITY_EXCLUDE_FROM_SALARY_CALCULATION_MUTATION,
  TOGGLE_ESTIMATE_ACTIVITY_EXCLUDE_FROM_SALARY_CALCULATION_MUTATION,
} from '@/modules/activities/mutations'
import type { ExcludeActivityPromptData } from '@/modules/salary/ExcludeActivityPrompt'
import { ExcludeActivityPrompt } from '@/modules/salary/ExcludeActivityPrompt'
import { UnpaidActivitiesTable } from '@/modules/salary/components/unpaid-activities-table'
import { PERMISSIONS } from '@/utils/permissions'
import {
  SPLIT_WORK_PERIOD_AT_GAP_MUTATION,
  SPLIT_WORK_PERIOD_MUTATION,
} from '@/modules/salary/mutations'
import { GRAPH_WORK_PERIOD } from '@/modules/salary/queries'
import { format, useActivityColor, usePermissions } from '@/utils'
import { useMutation, useQuery } from '@apollo/client'
import { Icon, usePrompt, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  addHours,
  differenceInMinutes,
  formatISO,
  isSameDay,
  max as maxDate,
  min as minDate,
  parseISO,
  subHours,
} from 'date-fns'
import groupBy from 'lodash/groupBy'
import { EyeIcon, EyeOffIcon, SplitIcon } from 'lucide-react'
import { useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useTheme } from 'styled-components'
import type { WorkPeriodNode } from '@/modules/salary/types.graphql'

const durationStringToMinutes = (duration?: string | null) =>
  duration
    ? new Time(...duration.split(':').map(s => parseFloat(s))).reduce(
        'seconds'
      ) / 60
    : undefined

const SalaryGantt: React.FC<{
  workPeriodId: string
  outerWorkPeriod?: WorkPeriodNode
  activitiesOnly?: boolean
  outerRefetch?: () => void
  fullWidth?: boolean
  /** Reports the activity bar under the cursor, for views linked to the chart. */
  onFeatureHover?: (feature: GanttFeature | null) => void
  /** Reports a clicked activity bar, for views that pin what the hover shows. */
  onFeatureSelect?: (feature: GanttFeature) => void
  /** Extra vertical markers, e.g. the map position the user is inspecting. */
  extraMarkers?: {
    id: string
    date: Date
    label: string
    color?: string
  }[]
}> = ({
  workPeriodId,
  outerWorkPeriod,
  activitiesOnly,
  outerRefetch,
  fullWidth,
  onFeatureHover,
  onFeatureSelect,
  extraMarkers,
}) => {
  const translations = useTranslate({
    duration: 'common.duration',
    minutes: 'common.minutes',
    nMinutes: ['common.n-minutes', { n: 0 }],
    activitiesForSalary: 'salary.activities-for-salary',
    activities: 'common.activities',
    timesheets: 'common.timesheets',
    removeForSalary: 'gantt.remove-for-salary-calculation',
    addForSalary: 'gantt.add-for-salary-calculation',
    comment: 'common.comment',
    ferryCrossing: 'gantt.ferry-crossing',
    ferryStart: 'gantt.ferry-start',
    ferryEnd: 'gantt.ferry-end',
    ferryMarkerUnpaired: 'gantt.ferry-marker-unpaired',
    splitAtTime: ['gantt.split-work-period-at-time', { time: '' }],
    splitAtGap: 'gantt.split-work-period-at-gap',
    splitSuccess: 'gantt.split-work-period-success',
    splitError: 'gantt.split-work-period-error',
    gaps: 'gantt.gaps',
    gapWithoutRegistrations: 'gantt.gap-without-registrations',
  })
  const activityTypeLabels = useActivityTypeLabels()
  const { data, refetch } = useQuery(GRAPH_WORK_PERIOD, {
    variables: { id: workPeriodId },
    skip: !!outerWorkPeriod,
  })
  const theme = useTheme()
  const getActivityColor = useActivityColor()
  const [zoom, setZoom] = useState(() => (isMobile ? 50 : 100))
  const [showSidebar, setShowSidebar] = useState(() => !isMobile)
  const addPrompt = usePrompt()
  const addToast = useToast()
  // The split moves activities between two periods and rebounds both, so the
  // month report and the surrounding view have to be re-read rather than
  // patched: a cache write here would have to reproduce the backend's own
  // bound arithmetic.
  const splitRefetch = {
    refetchQueries: ['SalaryMonthReport', 'TimesheetsReport'],
    awaitRefetchQueries: true,
  }
  const [splitWorkPeriod, { loading: splittingAtActivity }] = useMutation(
    SPLIT_WORK_PERIOD_MUTATION,
    splitRefetch
  )
  const [splitWorkPeriodAtGap, { loading: splittingAtGap }] = useMutation(
    SPLIT_WORK_PERIOD_AT_GAP_MUTATION,
    splitRefetch
  )
  const splittingWorkPeriod = splittingAtActivity || splittingAtGap
  const [toggleActivityPaid] = useMutation(
    TOGGLE_ACTIVITY_EXCLUDE_FROM_SALARY_CALCULATION_MUTATION,
    {
      update(cache, { data }) {
        const activity =
          data?.toggleActivityExcludeFromSalaryCalculation.activity
        if (!activity) return

        cache.modify({
          id: cache.identify(activity),
          fields: {
            excludeFromSalaryCalculation: () =>
              activity.excludeFromSalaryCalculation,
            excludeBefore: () => activity.excludeBefore,
            excludeAfter: () => activity.excludeAfter,
            excludeComment: () => activity.excludeComment,
          },
        })
      },
      onCompleted: () => {
        void refetch()
      },
    }
  )

  const [toggleEstimatePaid] = useMutation(
    TOGGLE_ESTIMATE_ACTIVITY_EXCLUDE_FROM_SALARY_CALCULATION_MUTATION,
    {
      update(cache, { data }) {
        const activity =
          data?.toggleEstimateActivityExcludeFromSalaryCalculation.activity
        if (!activity) return

        cache.modify({
          id: cache.identify(activity),
          fields: {
            excludeFromSalaryCalculation: () =>
              activity.excludeFromSalaryCalculation,
            excludeBefore: () => activity.excludeBefore,
            excludeAfter: () => activity.excludeAfter,
            excludeComment: () => activity.excludeComment,
          },
        })
      },
      onCompleted: () => {
        void refetch()
      },
    }
  )

  const { hasPermissions } = usePermissions()

  // Splitting rewrites payroll attribution rather than a period's metadata, so
  // it sits behind change_salarybase like the other salary edits -- and behind
  // allowAdmin, matching how the salary screens gate their own actions.
  const canSplitWorkPeriod = hasPermissions(
    PERMISSIONS.salary.change.salarybase,
    true
  )
  const canMarkActivitiesAsNotPaid = hasPermissions(
    PERMISSIONS.activities.change.driveractivitypaidstatus
  )

  if (!data?.workPeriod && !outerWorkPeriod) return <div>Loading...</div>

  const workPeriod = data?.workPeriod ?? outerWorkPeriod ?? null
  if (!workPeriod) return <div>Work period not found</div>
  const datetimeStart = parseISO(workPeriod?.datetimeStart ?? new Date())
  const datetimeEnd = parseISO(workPeriod?.datetimeEnd ?? new Date())
  const ActivityKey = 'Kjøredata'
  const EstimateKey = 'Estimert kjøredata'
  const TimesheetsKey = 'Timeliste'
  const GroupKey = 'Lønnsdata'

  const activityTooltipContent = (activity: {
    activityType: string
    datetimeStart: string
    datetimeEnd: string
    duration: number
    excludeComment?: string | null
  }) => (
    <div>
      <h3>
        {activityTypeLabels[activity.activityType]}{' '}
        {translations.nMinutes({
          n: Math.round((activity.duration / 60) * 100) / 100,
        })}
      </h3>
      <ul>
        <li>
          {format(parseISO(activity.datetimeStart), 'HH:mm')} -{' '}
          {format(parseISO(activity.datetimeEnd), 'HH:mm')}
        </li>
        {!!activity.excludeComment && (
          <li className="max-w-60 whitespace-pre-wrap">
            {translations.comment}: {activity.excludeComment}
          </li>
        )}
      </ul>
    </div>
  )

  const activityFeatures: Record<string, GanttFeature[]> = {
    [ActivityKey]: workPeriod.activities.map(activity => ({
      id: activity.id,
      name: '',
      startAt: parseISO(activity.datetimeStart),
      endAt: parseISO(activity.datetimeEnd),
      status: {
        id: activity.activityType,
        name: '',
        color: getActivityColor(activity),
        active: !activity.excludeFromSalaryCalculation,
      },
      duration: activity.duration / 60,
      group: {
        id: ActivityKey,
        name: ActivityKey,
        color: theme.colors.gray11,
      },
      manuallyEntered: activity.manuallyEntered,
      tooltipContent: activityTooltipContent(activity),
    })),
  }

  const estimateActivityFeatures: Record<string, GanttFeature[]> = {
    [EstimateKey]: workPeriod.estimateActivities.map(activity => ({
      id: activity.id,
      name: '',
      startAt: parseISO(activity.datetimeStart),
      endAt: parseISO(activity.datetimeEnd),
      status: {
        id: activity.activityType,
        name: '',
        color: getActivityColor(activity),
        active: !activity.excludeFromSalaryCalculation,
      },
      duration: activity.duration / 60,
      group: {
        id: EstimateKey,
        name: EstimateKey,
        color: theme.colors.gray11,
      },
      tooltipContent: activityTooltipContent(activity),
    })),
  }

  const salaryActivityFeatures: Record<string, GanttFeature[]> = {
    [translations.activitiesForSalary]: workPeriod.salaryActivities.map(
      activity => ({
        id: activity.id,
        name: '',
        startAt: parseISO(activity.datetimeStart),
        endAt: parseISO(activity.datetimeEnd),
        group: {
          id: translations.activitiesForSalary,
          name: translations.activitiesForSalary,
          color: getActivityColor(activity),
        },
        manuallyEntered: activity.manuallyEntered,
        tooltipContent: activityTooltipContent(activity),
      })
    ),
  }

  // A ferry crossing can end a work day at a rest far shorter than the eight-hour
  // gap, and the markers that did it usually sit outside the period's own bounds
  // (the backend hands over a window either side). They are pinned onto the
  // timeline the way infringements are: a crossing is a moment in the day, not
  // another kind of activity, so it gets no lane and no sidebar row of its own.
  const ferryConditions =
    'ferryConditions' in workPeriod ? workPeriod.ferryConditions : []
  const ferryPins = buildFerryPins(buildFerrySegments(ferryConditions))

  // A connected work day can span two dates, so a time is shown with its date
  // whenever it is not the day the work day began on.
  const timeOfDayLabel = (date: Date) =>
    isSameDay(date, datetimeStart)
      ? format(date, 'HH:mm')
      : format(date, 'dd.MM HH:mm')

  const ferryTooltip = ({ segment }: FerryPin) => (
    <div>
      <h3>
        {segment.unpaired
          ? translations.ferryMarkerUnpaired
          : translations.ferryCrossing}{' '}
        {segment.end &&
          translations.nMinutes({
            n: differenceInMinutes(segment.end, segment.start),
          })}
      </h3>
      <ul>
        <li>
          {timeOfDayLabel(segment.start)}
          {segment.end ? ` - ${timeOfDayLabel(segment.end)}` : ''}
        </li>
      </ul>
    </div>
  )

  const timeEntriesFeatures: Record<string, GanttFeature[]> = {
    [TimesheetsKey]: workPeriod.timeEntries
      .filter(entry => !!entry.datetimeEnd)
      .map(entry => ({
        id: entry.id,
        name: '',
        startAt: parseISO(entry.datetimeStart),
        endAt: entry.datetimeEnd
          ? parseISO(entry.datetimeEnd)
          : parseISO(entry.datetimeStart),
        group: {
          id: TimesheetsKey,
          name: TimesheetsKey,
          color: theme.colors.orange,
        },
        tooltipContent: (
          <div>
            <h3>
              {activityTypeLabels[DriverActivityType.WORK]}{' '}
              {Math.round(
                (new Time(
                  ...entry.totalWorkTime.split(':').map(s => parseFloat(s))
                ).reduce('seconds') /
                  60) *
                  100
              ) / 100}{' '}
              {translations.minutes}
            </h3>
            <ul>
              <li>
                {format(parseISO(entry.datetimeStart), 'HH:mm')} -{' '}
                {format(
                  entry.datetimeEnd
                    ? parseISO(entry.datetimeEnd)
                    : parseISO(entry.datetimeStart),
                  'HH:mm'
                )}
              </li>
              <li>{entry.comment}</li>
            </ul>
          </div>
        ),
      })),
  }

  // lockedAt is only selected on one of the two work-period shapes the gantt
  // accepts; the backend refuses a locked split either way.
  const lockedAt = 'lockedAt' in workPeriod ? workPeriod.lockedAt : null
  const canSplit = canSplitWorkPeriod && !lockedAt

  const gaps = canSplit
    ? findWorkPeriodGaps([
        ...workPeriod.activities,
        ...workPeriod.estimateActivities,
        ...workPeriod.timeEntries,
      ])
    : []
  const gapsById = new Map(
    gaps.map(gap => [`gap-${gap.start.toISOString()}`, gap])
  )
  const gapFeatures: GanttFeature[] = [...gapsById].map(([id, gap]) => {
    const minutes = differenceInMinutes(gap.end, gap.start)
    return {
      id,
      name: '',
      startAt: gap.start,
      endAt: gap.end,
      duration: minutes,
      group: {
        id: translations.gaps,
        name: translations.gaps,
        color: theme.colors.gray8,
      },
      tooltipContent: (
        <div>
          <h3>
            {translations.gapWithoutRegistrations}{' '}
            {translations.nMinutes({ n: minutes })}
          </h3>
          <ul>
            <li>
              {timeOfDayLabel(gap.start)} - {timeOfDayLabel(gap.end)}
            </li>
          </ul>
        </div>
      ),
    }
  })

  const workPeriodGroupedActivities = activitiesOnly
    ? []
    : workPeriod.groupedActivities.map((group, i) => {
        const duration =
          durationStringToMinutes(group.timeWorkTotal) ??
          differenceInMinutes(
            parseISO(group.endTime),
            parseISO(group.startTime)
          )

        return {
          id: (group.salaryId ?? '') + (group.startTime ?? '') + i.toString(),
          name: group.salaryName,
          startAt: parseISO(group.startTime),
          endAt: parseISO(group.endTime),
          duration,
          owner: null,
          group: {
            id: group.salaryName ?? '',
            name: group.salaryName ?? '',
            color: theme.colors.gray11,
          },
          tooltipContent: (
            <div>
              <h3>
                {group.salaryName ?? ''}{' '}
                {translations.nMinutes({
                  n: duration,
                })}
              </h3>
              <ul>
                <li>
                  {format(parseISO(group.startTime), 'HH:mm')} -{' '}
                  {format(parseISO(group.endTime), 'HH:mm')}
                </li>
              </ul>
            </div>
          ),
        }
      })
  const groupedActivityFeatures: Record<string, GanttFeature[]> = groupBy(
    workPeriodGroupedActivities,
    feature => feature.group.id
  )
  const featuresGroups: Record<string, Record<string, GanttFeature[]>> = {
    ...(activityFeatures
      ? { [translations.activities]: activityFeatures }
      : {}),
    ...(workPeriod.estimateActivities.length > 0
      ? { [EstimateKey]: estimateActivityFeatures }
      : {}),
    ...(workPeriod.timeEntries.length > 0
      ? { [translations.timesheets]: timeEntriesFeatures }
      : {}),
    ...(gapFeatures.length > 0
      ? { [translations.gaps]: { [translations.gaps]: gapFeatures } }
      : {}),
    [translations.activitiesForSalary]: salaryActivityFeatures,
    ...(activitiesOnly ? {} : { [GroupKey]: groupedActivityFeatures }),
  }
  const handleViewFeature = async (feature: GanttFeature) => {
    if (workPeriod.approvedByManager || !canMarkActivitiesAsNotPaid) return
    if (!feature.id) return

    // Estimate (placeholder) activities live on their own line but exclude the
    // same way as real ones: the prompt clips by before/after or disables the
    // whole block. The exclusion is carried forward by overlap on regeneration.
    const estimate = workPeriod.estimateActivities.find(
      a => a.id === feature.id
    )
    if (estimate) {
      const { data } = await addPrompt<ExcludeActivityPromptData | null>(
        resolve => (
          <ExcludeActivityPrompt activity={estimate} onSubmit={resolve} />
        )
      )
      if (!data) return
      await toggleEstimatePaid({
        variables: {
          activity: estimate.id,
          value: data.excludeFromSalaryCalculation,
          excludeBefore: data.excludeBefore
            ? formatISO(data.excludeBefore)
            : null,
          excludeAfter: data.excludeAfter ? formatISO(data.excludeAfter) : null,
          excludeComment: data.excludeComment,
        },
      })
      await outerRefetch?.()
      await refetch()
      return
    }

    const activity = workPeriod.activities.find(a => a.id === feature.id)
    if (!activity) return

    const { data } = await addPrompt<ExcludeActivityPromptData | null>(
      resolve => (
        <ExcludeActivityPrompt activity={activity} onSubmit={resolve} />
      )
    )

    if (!data) return
    await toggleActivityPaid({
      variables: {
        activity: activity.id,
        value: data.excludeFromSalaryCalculation,
        excludeBefore: data.excludeBefore
          ? formatISO(data.excludeBefore)
          : null,
        excludeAfter: data.excludeAfter ? formatISO(data.excludeAfter) : null,
        excludeComment: data.excludeComment,
      },
    })
    await outerRefetch?.()
    await refetch()
  }

  // Direct full-range toggle (no prompt) for the context menu: include or
  // exclude the whole activity/estimate by clearing before/after.
  // The activity a split would start the later work day with must be a real
  // activity (an estimate is a placeholder, and a split has to move stored rows)
  // and must not be the first one -- there would be nothing left before it.
  const canSplitAtActivity = (activityId: string) => {
    if (!canSplit) return false

    const activity = workPeriod.activities.find(a => a.id === activityId)
    if (!activity) return false

    const earliest = workPeriod.activities.reduce<string | null>(
      (earliestStart, candidate) =>
        !earliestStart || candidate.datetimeStart < earliestStart
          ? candidate.datetimeStart
          : earliestStart,
      null
    )
    return activity.datetimeStart !== earliest
  }

  // The split happens at the activity's start.
  const splitPointLabel = (activityId: string) => {
    const activity = workPeriod.activities.find(a => a.id === activityId)
    return activity ? timeOfDayLabel(parseISO(activity.datetimeStart)) : ''
  }

  const runSplit = async (split: () => Promise<unknown>) => {
    try {
      await split()
      addToast('success', translations.splitSuccess)
      await outerRefetch?.()
      await refetch()
    } catch (error) {
      addToast(
        'error',
        error instanceof Error && error.message
          ? error.message
          : translations.splitError
      )
    }
  }

  const splitAtActivity = (activityId: string) =>
    runSplit(() =>
      splitWorkPeriod({
        variables: { workPeriod: workPeriod.id, activity: activityId },
      })
    )

  // Any instant inside the gap will do: the later day begins with the first
  // content after it.
  const splitAtGap = (gap: WorkPeriodGap) =>
    runSplit(() =>
      splitWorkPeriodAtGap({
        variables: {
          workPeriod: workPeriod.id,
          at: formatISO((gap.start.getTime() + gap.end.getTime()) / 2),
        },
      })
    )

  const getContextMenuItems = (feature: GanttFeature) => {
    const featureId = feature.id
    if (!featureId) return []

    const gap = gapsById.get(featureId)
    if (gap) {
      return [
        {
          id: `split-work-period-${featureId}`,
          label: (
            <>
              <SplitIcon size={16} />
              {translations.splitAtGap}
            </>
          ),
          disabled: splittingWorkPeriod,
          onClick: () => void splitAtGap(gap),
        },
      ]
    }

    const items = []
    if (canSplitAtActivity(featureId)) {
      items.push({
        id: `split-work-period-${featureId}`,
        label: (
          <>
            <SplitIcon size={16} />
            {translations.splitAtTime({
              time: splitPointLabel(featureId),
            })}
          </>
        ),
        disabled: splittingWorkPeriod,
        onClick: () => void splitAtActivity(featureId),
      })
    }

    if (workPeriod.approvedByManager || !canMarkActivitiesAsNotPaid)
      return items

    const activity = workPeriod.activities.find(a => a.id === feature.id)
    const estimate = workPeriod.estimateActivities.find(
      a => a.id === feature.id
    )
    const target = activity ?? estimate
    if (!target) return items

    const isExcluded = target.excludeFromSalaryCalculation
    return [
      ...items,
      {
        id: `toggle-full-range-${feature.id}`,
        label: (
          <>
            {isExcluded ? <EyeIcon size={16} /> : <EyeOffIcon size={16} />}
            {isExcluded
              ? translations.addForSalary
              : translations.removeForSalary}
          </>
        ),
        onClick: async () => {
          const variables = {
            activity: target.id,
            value: !isExcluded,
            excludeBefore: null,
            excludeAfter: null,
            excludeComment: null,
          }
          if (activity) {
            await toggleActivityPaid({ variables })
          } else {
            await toggleEstimatePaid({ variables })
          }
          await outerRefetch?.()
          await refetch()
        },
      },
    ]
  }
  // Ferry markers reach beyond the period, so the chart has to reach with them
  // or the pin that explains the split is drawn off the edge.
  const ferryEdges = ferryPins.map(pin => pin.date)
  const startDatetime = subHours(minDate([datetimeStart, ...ferryEdges]), 1)
  const endDatetime = addHours(maxDate([datetimeEnd, ...ferryEdges]), 1)
  startDatetime.setMinutes(0, 0, 0)
  endDatetime.setMinutes(0, 0, 0)

  // Only show groups whose feature lists all have at least one feature.
  // The sidebar and the timeline must share this filter so their rows stay aligned;
  // otherwise empty groups (e.g. no salary activities) get a sidebar label but no lane.
  const visibleFeaturesGroups = Object.entries(featuresGroups).filter(
    ([, features]) => {
      const featureArray = Object.values(features)
      return featureArray.length > 0 && featureArray.every(f => f.length > 0)
    }
  )

  // Create object of featureKey and array of features
  const sidebarItems = visibleFeaturesGroups.flatMap(([, features]) =>
    Object.entries(features).map(([featureKey, features]) => ({
      featureKey,
      features,
    }))
  )
  const timelineMinHeight = `calc(var(--gantt-header-height) + (${
    sidebarItems.length
  } * var(--gantt-row-height)) + (${Math.max(
    sidebarItems.length - 1,
    0
  )} * 1rem))`
  return (
    <div
      style={{
        width: isMobile || fullWidth ? '100%' : `${1600}px`,
        maxWidth: '100%',
      }}
    >
      {/* The zoom controls and the total work stamp are positioned against the
          chart itself, so anything rendered below it stays clear of them. The
          bottom padding is the strip the stamp sits in. */}
      <div className="relative pb-4">
        {!isMobile && (
          <>
            <div
              className="absolute bg-grey"
              style={{ top: 10, right: 10, zIndex: 2000 }}
            >
              <button
                className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 cursor-pointer"
                onClick={() => setZoom(z => Math.max(20, z - 20))}
                disabled={zoom <= 20}
              >
                -
              </button>
              <span className="mx-2">{zoom}%</span>
              <button
                className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 cursor-pointer"
                onClick={() => setZoom(z => Math.min(500, z + 20))}
                disabled={zoom >= 500}
              >
                +
              </button>
            </div>
            <div
              className="absolute bg-grey"
              style={{ bottom: 10, right: 10, zIndex: 2000 }}
            >
              <b>
                {enforceHourMinuteString(
                  workPeriod.activityTimes?.timeWorkTotal
                )}
              </b>
            </div>
          </>
        )}
        {isMobile && (
          <button
            type="button"
            className="absolute top-2 left-2 z-[2000] flex h-10 w-10 items-center justify-center rounded border border-gray-200 bg-white/95"
            onClick={() => setShowSidebar(value => !value)}
            aria-label={translations.activities}
            title={translations.activities}
          >
            <Icon icon={showSidebar ? 'chevron-left' : 'chevron-right'} />
          </button>
        )}
        <GanttProvider
          className="border"
          range="daily"
          zoom={zoom}
          datetimeStart={startDatetime}
          datetimeEnd={endDatetime}
        >
          {showSidebar ? (
            <GanttSidebar compact={isMobile}>
              {sidebarItems.map(({ featureKey, features }, i) => (
                <GanttSidebarItem
                  feature={{
                    id: featureKey + i.toString(),
                    name: featureKey,
                    startAt: features[0]?.startAt ?? new Date(),
                    endAt: features[features.length - 1]?.endAt ?? new Date(),
                    duration: features.reduce(
                      (sum, feature) =>
                        sum +
                        (feature.duration === undefined
                          ? differenceInMinutes(feature.endAt, feature.startAt)
                          : feature.duration),
                      0
                    ),
                    status: undefined,
                  }}
                  key={featureKey + i.toString()}
                />
              ))}
            </GanttSidebar>
          ) : (
            <div aria-hidden="true" style={{ minHeight: timelineMinHeight }} />
          )}
          <GanttTimeline
            className="min-h-full"
            style={{ minHeight: timelineMinHeight }}
          >
            <GanttHeader />
            {ferryPins.map(pin => (
              <GanttMarker
                key={pin.id}
                id={pin.id}
                date={pin.date}
                label={
                  pin.kind === 'start'
                    ? translations.ferryStart
                    : translations.ferryEnd
                }
                color={
                  pin.segment.unpaired
                    ? theme.colors.blue700
                    : theme.colors.blue400
                }
                tooltipContent={ferryTooltip(pin)}
              />
            ))}
            {extraMarkers?.map(marker => (
              <GanttMarker
                key={marker.id}
                id={marker.id}
                date={marker.date}
                label={marker.label}
                color={marker.color}
              />
            ))}
            <GanttFeatureList>
              {visibleFeaturesGroups.map(([group, features]) => {
                const featureArray = Object.values(features)
                // Only the tachograph lanes carry one bar per activity. The
                // salary, timesheet and trip lanes roll a whole day into a few
                // bars, so linking them to a map would light up everything.
                const isActivityLane =
                  group === translations.activities || group === EstimateKey

                return featureArray.map((featureList, i) => (
                  <GanttFeatureRow
                    key={`${workPeriod.id}-${group}-${i}`}
                    features={featureList}
                    handleViewFeature={feature => handleViewFeature(feature)}
                    onFeatureHover={isActivityLane ? onFeatureHover : undefined}
                    onFeatureClick={
                      isActivityLane ? onFeatureSelect : undefined
                    }
                    getContextMenuItems={getContextMenuItems}
                  />
                ))
              })}
            </GanttFeatureList>
          </GanttTimeline>
        </GanttProvider>
      </div>
      <UnpaidActivitiesTable workPeriod={workPeriod} />
    </div>
  )
}
export default SalaryGantt
